import React from 'react';
import { Badge, Table } from 'react-bootstrap';
import { AmlCase, AmlFilter } from './types';
import { primaryName, secondaryContact } from './formatUser';
import {
  STATUS_LABELS, STATUS_BADGE,
  RISK_LABELS, RISK_BADGE,
  formatDateTime, formatAmount, scoreColor,
} from './amlUtils';

interface Props {
  cases: AmlCase[];
  selectedId: string | null;
  onSelect: (c: AmlCase) => void;
  filter: AmlFilter;
  onFilterChange: (f: Partial<AmlFilter>) => void;
  total: number;
}

// Таблица AML-кейсов с фильтрацией и пагинацией
const AmlCaseList: React.FC<Props> = ({
  cases,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  total,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / filter.size));

  return (
    <div>
      {/* Фильтры */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto', minWidth: 150 }}
          value={filter.status || ''}
          onChange={(e) => onFilterChange({ status: e.target.value as any, page: 1 })}
        >
          <option value="">Все статусы</option>
          <option value="active">Активные (ожидает + на проверке)</option>
          <option value="pending">Ожидает</option>
          <option value="in_review">На проверке</option>
          <option value="approved">Одобрено</option>
          <option value="rejected">Отклонено</option>
          <option value="escalated">Эскалировано</option>
          <option value="overdue">Просрочено</option>
        </select>

        <select
          className="form-select form-select-sm"
          style={{ width: 'auto', minWidth: 150 }}
          value={filter.riskLevel || ''}
          onChange={(e) => onFilterChange({ riskLevel: e.target.value as any, page: 1 })}
        >
          <option value="">Все риски</option>
          <option value="low">Низкий</option>
          <option value="medium">Средний</option>
          <option value="high">Высокий</option>
          <option value="critical">Критический</option>
        </select>

        <input
          type="text"
          className="form-control form-control-sm"
          style={{ width: 200 }}
          placeholder="Поиск по ID, email, имени..."
          value={filter.search || ''}
          onChange={(e) => onFilterChange({ search: e.target.value, page: 1 })}
        />
      </div>

      {/* Таблица */}
      <Table
        hover
        responsive
        size="sm"
        className="align-middle"
        style={{ fontSize: '0.82rem' }}
      >
        <thead className="table-light">
          <tr>
            <th>ID кейса</th>
            <th>Пользователь</th>
            <th>Статус</th>
            <th>Риск</th>
            <th className="text-center">Скор</th>
            <th>Сумма</th>
            <th>Правил</th>
            <th>Создан</th>
            <th>Срок</th>
          </tr>
        </thead>
        <tbody>
          {cases.length === 0 ? (
            <tr>
              <td colSpan={9} className="text-center text-muted py-4">
                Нет кейсов по выбранным фильтрам
              </td>
            </tr>
          ) : (
            cases.map((c) => (
              <tr
                key={c.id}
                onClick={() => onSelect(c)}
                style={{ cursor: 'pointer' }}
                className={selectedId === c.id ? 'table-primary' : ''}
              >
                <td>
                  <code style={{ fontSize: '0.78rem' }}>{c.caseId || c.id}</code>
                </td>
                <td>
                  <div className="fw-semibold">{primaryName(c)}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {secondaryContact(c) || '—'}
                  </div>
                </td>
                <td>
                  <Badge bg={STATUS_BADGE[c.status]}>
                    {STATUS_LABELS[c.status]}
                  </Badge>
                </td>
                <td>
                  <Badge bg={RISK_BADGE[c.riskLevel]}>
                    {RISK_LABELS[c.riskLevel]}
                  </Badge>
                </td>
                <td className="text-center">
                  <span className={`fw-bold text-${scoreColor(c.riskScore)}`}>
                    {c.riskScore}
                  </span>
                </td>
                <td>
                  {formatAmount(c.transaction.amount, c.transaction.currency)}
                </td>
                <td className="text-center">
                  <Badge bg="secondary">{c.triggeredRules.length || c.rulesCount || 0}</Badge>
                </td>
                <td className="text-muted">{formatDateTime(c.createdAt)}</td>
                <td>
                  <span
                    className={
                      new Date(c.dueAt) < new Date() &&
                      !['approved', 'rejected'].includes(c.status)
                        ? 'text-danger fw-semibold'
                        : 'text-muted'
                    }
                  >
                    {formatDateTime(c.dueAt)}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Пагинация */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <span className="text-muted small">
          Всего: {total} кейс{total === 1 ? '' : total < 5 ? 'а' : 'ов'}
        </span>

        <div className="d-flex gap-1 align-items-center">
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={filter.page <= 1}
            onClick={() => onFilterChange({ page: filter.page - 1 })}
          >
            ‹
          </button>

          <span className="small px-2">
            {filter.page} / {totalPages}
          </span>

          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={filter.page >= totalPages}
            onClick={() => onFilterChange({ page: filter.page + 1 })}
          >
            ›
          </button>

          <select
            className="form-select form-select-sm ms-2"
            style={{ width: 'auto' }}
            value={filter.size}
            onChange={(e) => onFilterChange({ size: Number(e.target.value), page: 1 })}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default AmlCaseList;
